import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  Content,
  ExpandableSection,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Split,
  SplitItem,
  Stack,
  StackItem,
  TextInput,
} from '@patternfly/react-core';
import { MagicIcon, MinusCircleIcon, PlusCircleIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useMailDomainDnsZone } from '../../../hooks/useMailDomainDnsZone';
import { rrsetErrorMessage } from '../Domains/zoneRecords';
import { unwrapTxtValue } from './spfParse';
import {
  DMARC_DEFAULT_FO,
  DMARC_DEFAULT_PCT,
  DMARC_DEFAULT_RF,
  DMARC_DEFAULT_RI,
  DMARC_FAILURE_OPTIONS,
  type DmarcAlignment,
  type DmarcBuilderState,
  type DmarcFailureOption,
  type DmarcPolicy,
  type DmarcSubdomainPolicy,
  dmarcToBuilder,
  parseDmarc,
  serializeDmarc,
} from './dmarcParse';
import type { ApiResponseDnsRrsetWrite } from '../../../types/api/ApiResponseDomainName';
import type { ApiResponseMailDomainDnsZone } from '../../../types/api/ApiResponseMailDomain';

interface DmarcWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Bridge to the DNS side — the dns-zone state route is keyed by this. */
  organizationDomainId: number;
  domainName: string;
  /** The currently published DMARC policy, used to prefill the builder (or `null` when none). */
  currentValue: string | null;
  /** Called after a successful publish so the DMARC tab refetches. */
  onPublished: () => void;
}

/** One editable report-address row (rua/ruf). `id` is a stable React key owned by the component. */
interface AddressRow {
  id: string;
  value: string;
}

const POLICY_OPTIONS: { value: DmarcPolicy; label: string }[] = [
  { value: 'none', label: 'None — monitor only (no enforcement)' },
  { value: 'quarantine', label: 'Quarantine — send failing mail to spam' },
  { value: 'reject', label: 'Reject — block failing mail' },
];

const SUBDOMAIN_POLICY_OPTIONS: { value: DmarcSubdomainPolicy; label: string }[] = [
  { value: 'inherit', label: 'Same as domain policy' },
  { value: 'none', label: 'None — monitor only' },
  { value: 'quarantine', label: 'Quarantine' },
  { value: 'reject', label: 'Reject' },
];

const ALIGNMENT_OPTIONS: { value: DmarcAlignment; label: string }[] = [
  { value: 'r', label: 'Relaxed (default)' },
  { value: 's', label: 'Strict' },
];

const FAILURE_OPTION_LABELS: Record<DmarcFailureOption, string> = {
  '0': '0 — when SPF and DKIM both fail (default)',
  '1': '1 — when SPF or DKIM fails',
  d: 'd — on a DKIM failure',
  s: 's — on an SPF failure',
};

const POLICY_HINTS: Record<DmarcPolicy, string> = {
  none: 'Receivers take no action — you only collect reports. The safe starting point while rolling out.',
  quarantine: 'Receivers send failing mail to spam/quarantine.',
  reject: 'Receivers reject failing mail outright — the strongest protection.',
};

/** Light, forgiving client-side check for a report address (the backend validates authoritatively). */
function validateAddress(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Enter an address.';
  if (/\s/.test(v)) return 'Cannot contain spaces.';
  // A mailto: URI must carry a real address — "mailto:" alone (or with a malformed local part) is not valid.
  if (/^mailto:/i.test(v)) {
    return /^mailto:[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(v)
      ? null
      : 'Enter a valid mailto: address (e.g. mailto:dmarc@example.com).';
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null; // another URI scheme (https:, …)
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return null; // a bare email → mailto: is added on serialize
  return 'Enter an email address (e.g. dmarc@example.com) or a URI.';
}

const isPct = (v: string) => /^\d+$/.test(v.trim()) && Number(v) >= 0 && Number(v) <= 100;
const isNonNegInt = (v: string) => /^\d+$/.test(v.trim());

/** Normalize an RRSet name for comparison (drop a trailing dot, lower-case). */
const normName = (n: string) => n.replace(/\.$/, '').toLowerCase();

/**
 * Guided DMARC-record builder. The user sets the requested policy, report addresses, alignment and the
 * advanced tuning tags; the resulting record string is previewed live at the top. When the domain's DNS
 * zone is managed here it can publish the record as a TXT record at the `_dmarc` label (preserving any
 * non-DMARC TXT siblings there); otherwise it explains that and offers the record for manual copy.
 */
function DmarcWizardModal({
  isOpen,
  onClose,
  organizationDomainId,
  domainName,
  currentValue,
  onPublished,
}: DmarcWizardModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { data: dz, loading: dnsLoading, error: dnsError, refetch: refetchDns } = useMailDomainDnsZone(
    organizationDomainId,
    isOpen,
  );

  const [policy, setPolicy] = useState<DmarcPolicy>('none');
  const [subdomainPolicy, setSubdomainPolicy] = useState<DmarcSubdomainPolicy>('inherit');
  const [ruaRows, setRuaRows] = useState<AddressRow[]>([]);
  const [rufRows, setRufRows] = useState<AddressRow[]>([]);
  const [pctStr, setPctStr] = useState('');
  const [adkim, setAdkim] = useState<DmarcAlignment>('r');
  const [aspf, setAspf] = useState<DmarcAlignment>('r');
  const [fo, setFo] = useState<DmarcFailureOption>(DMARC_DEFAULT_FO);
  const [riStr, setRiStr] = useState('');
  const [rfStr, setRfStr] = useState('');
  const [unsupported, setUnsupported] = useState<string[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idCounter = useRef(0);
  // Read `currentValue` non-reactively so the seed effect runs only on the open transition — a refetch
  // that replaces currentValue while the modal is open must not wipe the user's in-progress edits.
  const currentValueRef = useRef(currentValue);
  currentValueRef.current = currentValue;

  // Reset and seed the builder from the current record each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const b = dmarcToBuilder(currentValueRef.current);
    setPolicy(b.policy);
    setSubdomainPolicy(b.subdomainPolicy);
    setRuaRows(b.rua.map((v) => ({ id: `rua-${idCounter.current++}`, value: v })));
    setRufRows(b.ruf.map((v) => ({ id: `ruf-${idCounter.current++}`, value: v })));
    setPctStr(b.pct === DMARC_DEFAULT_PCT ? '' : String(b.pct));
    setAdkim(b.adkim);
    setAspf(b.aspf);
    setFo(b.fo);
    setRiStr(b.ri === DMARC_DEFAULT_RI ? '' : String(b.ri));
    setRfStr(b.rf.toLowerCase() === DMARC_DEFAULT_RF ? '' : b.rf);
    setUnsupported(b.unsupported);
    // Reveal the advanced section up front when the existing record already populated it.
    setAdvancedOpen(
      b.ruf.length > 0 || b.fo !== DMARC_DEFAULT_FO || b.ri !== DMARC_DEFAULT_RI || b.rf.toLowerCase() !== DMARC_DEFAULT_RF,
    );
    setCopied(false);
    setError(null);
    setIsSubmitting(false);
  }, [isOpen]);

  const ruaErrors = useMemo(() => ruaRows.map((r) => validateAddress(r.value)), [ruaRows]);
  const rufErrors = useMemo(() => rufRows.map((r) => validateAddress(r.value)), [rufRows]);
  const pctError = pctStr.trim() !== '' && !isPct(pctStr) ? 'Enter a whole number from 0 to 100.' : null;
  const riError = riStr.trim() !== '' && !isNonNegInt(riStr) ? 'Enter a non-negative whole number of seconds.' : null;
  const hasErrors =
    ruaErrors.some((e) => e !== null) || rufErrors.some((e) => e !== null) || pctError !== null || riError !== null;

  // The builder state the preview + publish derive from. Invalid pct/ri fall back to their default in the
  // preview (the inline error still blocks Publish) so the preview never shows `pct=NaN`.
  const builderState: DmarcBuilderState = useMemo(
    () => ({
      policy,
      subdomainPolicy,
      rua: ruaRows.map((r) => r.value),
      ruf: rufRows.map((r) => r.value),
      pct: isPct(pctStr) ? Number(pctStr) : DMARC_DEFAULT_PCT,
      adkim,
      aspf,
      fo,
      ri: isNonNegInt(riStr) ? Number(riStr) : DMARC_DEFAULT_RI,
      rf: rfStr,
      unsupported,
    }),
    [policy, subdomainPolicy, ruaRows, rufRows, pctStr, adkim, aspf, fo, riStr, rfStr, unsupported],
  );

  const preview = useMemo(() => serializeDmarc(builderState), [builderState]);
  const previewWarnings = useMemo(() => parseDmarc(preview).warnings, [preview]);

  const canPublish = dz?.zone_state === 'configured' && dz.domain_name_id !== null;

  const addAddress = (setter: React.Dispatch<React.SetStateAction<AddressRow[]>>, prefix: string) =>
    setter((prev) => [...prev, { id: `${prefix}-${idCounter.current++}`, value: '' }]);
  const removeAddress = (setter: React.Dispatch<React.SetStateAction<AddressRow[]>>, id: string) =>
    setter((prev) => prev.filter((r) => r.id !== id));
  const updateAddress = (setter: React.Dispatch<React.SetStateAction<AddressRow[]>>, id: string, value: string) =>
    setter((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)));
  const onCopy = (text: string) => {
    void navigator.clipboard?.writeText(text);
    setCopied(true);
  };

  const handlePublish = async () => {
    if (!dz || dz.domain_name_id === null) return;
    setIsSubmitting(true);
    setError(null);

    const apex = dz.domain;
    const recordName = `_dmarc.${apex}`;
    const domainNameId = dz.domain_name_id;
    const records = dz.zone?.records ?? [];
    // Preserve any non-DMARC TXT records that somehow share the `_dmarc` label — replace only the DMARC
    // line. `_dmarc` is a dedicated label so siblings are rare, but mirroring the SPF write keeps it safe.
    const dmarcTxt = records.find((r) => r.type.toUpperCase() === 'TXT' && normName(r.name) === normName(recordName));
    const siblings = (dmarcTxt?.content ?? []).filter(
      (v) => !unwrapTxtValue(v).toLowerCase().startsWith('v=dmarc1'),
    );
    const payload = { name: recordName, type: 'TXT', ttl: dmarcTxt?.ttl ?? 3600, content: [...siblings, preview] };

    const send = (method: 'POST' | 'PATCH') =>
      apiFetch(`/datacenters/domain-names/${domainNameId}/dns/rrset`, {
        method,
        headers: { Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

    const notifyFailure = (reason: string) => {
      setError(reason);
      addNotification({
        title: 'Could not publish DMARC record',
        srTitle: 'Error alert',
        description: reason,
        variant: 'danger',
      });
      setDrawerOpen(true);
    };

    try {
      let method: 'POST' | 'PATCH' = dmarcTxt ? 'PATCH' : 'POST';
      let response = await send(method);
      let body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;

      // Collision fallback: the open-time snapshot was stale (a `_dmarc` TXT set now exists). Re-read the
      // zone so we merge with the REAL current TXT siblings instead of overwriting them, then replace in place.
      const collided =
        method === 'POST' &&
        body?.status !== 'success' &&
        typeof body?.data?.name === 'string' &&
        body.data.name.toLowerCase().includes('already exists');
      if (collided) {
        try {
          const freshResp = await apiFetch(
            `/emails/domains/dns-zone?organization_domain_id=${organizationDomainId}`,
          );
          const freshBody = (await freshResp.json().catch(() => null)) as ApiResponseMailDomainDnsZone | null;
          const freshRecords =
            freshBody?.status === 'success' ? freshBody.data?.dns_zone.zone?.records ?? [] : [];
          const freshDmarcTxt = freshRecords.find(
            (r) => r.type.toUpperCase() === 'TXT' && normName(r.name) === normName(recordName),
          );
          if (freshDmarcTxt) {
            payload.content = [
              ...freshDmarcTxt.content.filter((v) => !unwrapTxtValue(v).toLowerCase().startsWith('v=dmarc1')),
              preview,
            ];
            payload.ttl = freshDmarcTxt.ttl;
          }
        } catch {
          // Re-read failed — fall through to a plain replace with the payload we already have.
        }
        method = 'PATCH';
        response = await send('PATCH');
        body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;
      }

      if (body?.status === 'success' && body.data?.record_set) {
        addNotification({
          title: 'DMARC record published',
          srTitle: 'Success alert',
          description: `The DMARC record for ${apex} was published to DNS.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onPublished();
        onClose();
        return;
      }
      notifyFailure(rrsetErrorMessage(body) ?? `Request failed with status ${response.status}`);
    } catch (err) {
      notifyFailure(err instanceof Error ? err.message : 'An unexpected error occurred while publishing.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAddressRows = (
    rows: AddressRow[],
    errors: (string | null)[],
    setter: React.Dispatch<React.SetStateAction<AddressRow[]>>,
    prefix: string,
    fieldId: string,
    addLabel: string,
    placeholder: string,
  ) => (
    <Stack hasGutter>
      {rows.map((row, i) => (
        <StackItem key={row.id}>
          <Split hasGutter>
            <SplitItem isFilled>
              <TextInput
                id={i === 0 ? fieldId : undefined}
                type="text"
                value={row.value}
                onChange={(_event, value) => updateAddress(setter, row.id, value)}
                aria-label={`${addLabel} ${i + 1}`}
                placeholder={placeholder}
                validated={errors[i] ? 'error' : 'default'}
                isDisabled={isSubmitting}
              />
            </SplitItem>
            <SplitItem>
              <Button
                variant="plain"
                aria-label={`Remove address ${i + 1}`}
                icon={<MinusCircleIcon />}
                onClick={() => removeAddress(setter, row.id)}
                isDisabled={isSubmitting}
              />
            </SplitItem>
          </Split>
          {errors[i] && (
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant="error">{errors[i]}</HelperTextItem>
              </HelperText>
            </FormHelperText>
          )}
        </StackItem>
      ))}
      <StackItem>
        <Button
          variant="link"
          icon={<PlusCircleIcon />}
          onClick={() => addAddress(setter, prefix)}
          isDisabled={isSubmitting}
        >
          {addLabel}
        </Button>
      </StackItem>
    </Stack>
  );

  const renderDnsSection = () => {
    if (dnsLoading) {
      return (
        <Bullseye className="pf-v6-u-mt-md">
          <Spinner size="md" aria-label="Checking the DNS zone" />
        </Bullseye>
      );
    }
    if (dnsError) {
      return (
        <Alert
          variant="warning"
          isInline
          title="Couldn't check the DNS zone"
          className="pf-v6-u-mt-md"
          actionLinks={<AlertActionLink onClick={() => void refetchDns()}>Retry</AlertActionLink>}
        >
          {dnsError} You can still copy the record above and add it at your DNS provider as a TXT record at{' '}
          <code>_dmarc.{domainName}</code>.
        </Alert>
      );
    }
    if (!canPublish) {
      return (
        <Alert variant="info" isInline title="This zone isn't managed by the Manager" className="pf-v6-u-mt-md">
          {dz?.zone_error ? `${dz.zone_error} ` : ''}
          The record can&apos;t be saved to DNS directly. Copy it above and add it at your DNS provider as a
          TXT record at <code>_dmarc.{domainName}</code>.
        </Alert>
      );
    }
    const zoneRecords = dz?.zone?.records ?? [];
    const dmarcTxtExists = zoneRecords.some(
      (r) =>
        r.type.toUpperCase() === 'TXT' &&
        normName(r.name) === normName(`_dmarc.${dz!.domain}`) &&
        r.content.some((c) => unwrapTxtValue(c).toLowerCase().startsWith('v=dmarc1')),
    );
    return (
      <Alert variant="info" isInline isPlain title="Ready to publish" className="pf-v6-u-mt-md">
        Publishing writes this record as a TXT record at <code>_dmarc.{domainName}</code>.
        {dmarcTxtExists ? ' The existing DMARC record is replaced.' : ''}
      </Alert>
    );
  };

  const renderFooter = () => {
    if (dnsLoading) {
      return (
        <Button variant="link" onClick={onClose}>
          Cancel
        </Button>
      );
    }
    if (canPublish) {
      return (
        <>
          <Button
            variant="primary"
            icon={<MagicIcon />}
            onClick={() => void handlePublish()}
            isLoading={isSubmitting}
            isDisabled={isSubmitting || hasErrors}
          >
            Publish to DNS
          </Button>
          <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
        </>
      );
    }
    return (
      <Button variant="primary" onClick={onClose}>
        Close
      </Button>
    );
  };

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      aria-label={`Build DMARC record for ${domainName}`}
    >
      <ModalHeader title="Build DMARC record" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Could not publish" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}

        <Content component="h2" className="pf-v6-u-font-size-sm pf-v6-u-mb-xs">
          Resulting record
        </Content>
        <CodeBlock
          actions={
            <CodeBlockAction>
              <ClipboardCopyButton
                id="dmarc-wizard-copy"
                aria-label="Copy DMARC record to clipboard"
                variant="plain"
                onClick={() => onCopy(preview)}
                exitDelay={copied ? 1500 : 600}
                maxWidth="110px"
                onTooltipHidden={() => setCopied(false)}
              >
                {copied ? 'Copied!' : 'Copy to clipboard'}
              </ClipboardCopyButton>
            </CodeBlockAction>
          }
        >
          <CodeBlockCode>{preview}</CodeBlockCode>
        </CodeBlock>
        {previewWarnings.length > 0 && (
          <FormHelperText>
            <HelperText>
              {previewWarnings.map((w, i) => (
                <HelperTextItem key={i} variant="warning">
                  {w}
                </HelperTextItem>
              ))}
            </HelperText>
          </FormHelperText>
        )}

        {unsupported.length > 0 && (
          <Alert
            variant="warning"
            isInline
            title="Some of the current record can't be edited here"
            className="pf-v6-u-mt-md"
          >
            The builder can&apos;t represent{' '}
            <code style={{ wordBreak: 'break-all' }}>{unsupported.join('; ')}</code>. If you publish, the
            record is replaced and those parts are dropped.
          </Alert>
        )}

        <Form className="pf-v6-u-mt-md">
          <FormGroup label="Policy" fieldId="dmarc-policy" isRequired>
            <FormSelect
              id="dmarc-policy"
              value={policy}
              onChange={(_event, value) => setPolicy(value as DmarcPolicy)}
              isDisabled={isSubmitting}
              aria-label="DMARC policy"
            >
              {POLICY_OPTIONS.map((o) => (
                <FormSelectOption key={o.value} value={o.value} label={o.label} />
              ))}
            </FormSelect>
            <FormHelperText>
              <HelperText>
                <HelperTextItem>{POLICY_HINTS[policy]}</HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Subdomain policy" fieldId="dmarc-sp">
            <FormSelect
              id="dmarc-sp"
              value={subdomainPolicy}
              onChange={(_event, value) => setSubdomainPolicy(value as DmarcSubdomainPolicy)}
              isDisabled={isSubmitting}
              aria-label="DMARC subdomain policy"
            >
              {SUBDOMAIN_POLICY_OPTIONS.map((o) => (
                <FormSelectOption key={o.value} value={o.value} label={o.label} />
              ))}
            </FormSelect>
          </FormGroup>

          <FormGroup label="Aggregate report addresses (rua)" fieldId="dmarc-rua">
            {renderAddressRows(
              ruaRows,
              ruaErrors,
              setRuaRows,
              'rua',
              'dmarc-rua',
              'Add aggregate report address',
              'dmarc@example.com',
            )}
            <FormHelperText>
              <HelperText>
                <HelperTextItem>Where receivers send daily aggregate (statistical) DMARC reports.</HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Apply to (pct)" fieldId="dmarc-pct">
            <TextInput
              id="dmarc-pct"
              type="text"
              inputMode="numeric"
              value={pctStr}
              onChange={(_event, value) => setPctStr(value)}
              placeholder="100"
              aria-label="Percentage of mail the policy applies to"
              validated={pctError ? 'error' : 'default'}
              isDisabled={isSubmitting}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={pctError ? 'error' : 'default'}>
                  {pctError ?? 'Percentage of mail the policy applies to (default 100).'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="DKIM alignment (adkim)" fieldId="dmarc-adkim">
            <FormSelect
              id="dmarc-adkim"
              value={adkim}
              onChange={(_event, value) => setAdkim(value as DmarcAlignment)}
              isDisabled={isSubmitting}
              aria-label="DKIM alignment mode"
            >
              {ALIGNMENT_OPTIONS.map((o) => (
                <FormSelectOption key={o.value} value={o.value} label={o.label} />
              ))}
            </FormSelect>
          </FormGroup>

          <FormGroup label="SPF alignment (aspf)" fieldId="dmarc-aspf">
            <FormSelect
              id="dmarc-aspf"
              value={aspf}
              onChange={(_event, value) => setAspf(value as DmarcAlignment)}
              isDisabled={isSubmitting}
              aria-label="SPF alignment mode"
            >
              {ALIGNMENT_OPTIONS.map((o) => (
                <FormSelectOption key={o.value} value={o.value} label={o.label} />
              ))}
            </FormSelect>
          </FormGroup>

          <ExpandableSection
            toggleTextCollapsed="Show advanced options"
            toggleTextExpanded="Hide advanced options"
            isExpanded={advancedOpen}
            onToggle={(_event, expanded) => setAdvancedOpen(expanded)}
          >
            <FormGroup label="Failure report addresses (ruf)" fieldId="dmarc-ruf">
              {renderAddressRows(
                rufRows,
                rufErrors,
                setRufRows,
                'ruf',
                'dmarc-ruf',
                'Add failure report address',
                'forensic@example.com',
              )}
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="warning">
                    Failure (forensic) reports can include message headers and content (PII). Only add an
                    address you are authorized to send this data to.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>

            <FormGroup label="Failure options (fo)" fieldId="dmarc-fo">
              <FormSelect
                id="dmarc-fo"
                value={fo}
                onChange={(_event, value) => setFo(value as DmarcFailureOption)}
                isDisabled={isSubmitting}
                aria-label="Failure reporting options"
              >
                {DMARC_FAILURE_OPTIONS.map((o) => (
                  <FormSelectOption key={o} value={o} label={FAILURE_OPTION_LABELS[o]} />
                ))}
              </FormSelect>
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>Only applies when a failure report address (ruf) is set.</HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>

            <FormGroup label="Report interval (ri)" fieldId="dmarc-ri">
              <TextInput
                id="dmarc-ri"
                type="text"
                inputMode="numeric"
                value={riStr}
                onChange={(_event, value) => setRiStr(value)}
                placeholder="86400"
                aria-label="Aggregate report interval in seconds"
                validated={riError ? 'error' : 'default'}
                isDisabled={isSubmitting}
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={riError ? 'error' : 'default'}>
                    {riError ?? 'Requested seconds between aggregate reports (default 86400 = 1 day).'}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>

            <FormGroup label="Report format (rf)" fieldId="dmarc-rf">
              <TextInput
                id="dmarc-rf"
                type="text"
                value={rfStr}
                onChange={(_event, value) => setRfStr(value)}
                placeholder="afrf"
                aria-label="Failure report format"
                isDisabled={isSubmitting}
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>Failure report format (default afrf — the only widely supported value).</HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          </ExpandableSection>
        </Form>

        {renderDnsSection()}
      </ModalBody>
      <ModalFooter>{renderFooter()}</ModalFooter>
    </Modal>
  );
}

export default DmarcWizardModal;
